import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  Tooltip,
  useDisclosure,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import {
  CreditAgreement,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const recurringDateConfigFieldPlaceholder =
  'All configured recurring dates have already been added';
const placeholderSelectOptions = [
  {
    value: recurringDateConfigFieldPlaceholder,
    label: recurringDateConfigFieldPlaceholder,
  },
];

type AthenaRecurringDatesContainerParams = {
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  recurringQuery: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  fullPropertyKey: string;
  wipConfig?: WipConfig;
};
export const AthenaRecurringDatesContainer = ({
  globalRecurringDateConfigs,
  recurringQuery,
  fullPropertyKey,
  wipConfig,
}: AthenaRecurringDatesContainerParams) => {
  const theme = useTheme();
  const textColor = useMemo(() => {
    return theme.config.initialColorMode === 'dark'
      ? 'white'
      : colors.black[70];
  }, [theme]);

  const {
    isOpen: isEditModeOpen,
    onOpen: onEditModeOpen,
    onClose: onEditModeClose,
  } = useDisclosure();

  const [
    latestEditedRecurringDateConfigFields,
    setLatestEditedRecurringDateConfigFields,
  ] = useState<string[]>(recurringQuery.globalRecurringDateConfigFields);
  const [
    isSaveRecurringDateConfigsLoading,
    setIsSaveRecurringDateConfigsLoading,
  ] = useState<boolean>(false);

  const onAddRecurringDateConfigField = useCallback(
    ({
      newRecurringDateConfigField,
    }: {
      newRecurringDateConfigField: string;
    }) => {
      const fieldsStateCopy = _.cloneDeep(
        latestEditedRecurringDateConfigFields,
      );
      fieldsStateCopy.push(newRecurringDateConfigField);
      setLatestEditedRecurringDateConfigFields(fieldsStateCopy);
    },
    [latestEditedRecurringDateConfigFields],
  );
  const onDeleteRecurringDateConfigField = useCallback(
    ({
      recurringDateConfigFieldToDelete,
    }: {
      recurringDateConfigFieldToDelete: string;
    }) => {
      const newFieldsWithoutFieldToDelete = _.without(
        latestEditedRecurringDateConfigFields,
        recurringDateConfigFieldToDelete,
      );
      setLatestEditedRecurringDateConfigFields(newFieldsWithoutFieldToDelete);
    },
    [latestEditedRecurringDateConfigFields],
  );

  const onSaveRecurringDateConfig = useCallback(async (): Promise<void> => {
    if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
      const wipRecurringQuery = getIn(
        wipConfig.formProps.values,
        fullPropertyKey,
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
          wipRecurringQuery,
        ),
        `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
      );

      const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
      recurringQueryCopy.globalRecurringDateConfigFields = latestEditedRecurringDateConfigFields;

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: fullPropertyKey,
        updatedProperty: recurringQueryCopy,
        setIsSaveLoading: setIsSaveRecurringDateConfigsLoading,
      });
    }
  }, [wipConfig, fullPropertyKey, latestEditedRecurringDateConfigFields]);

  const selectOptions: {
    value: string;
    label: string;
  }[] = useMemo(() => {
    const allGlobalRecurringDateConfigFields = _.map(
      globalRecurringDateConfigs,
      ({ field }) => field,
    );

    return _.compact(
      _.map(allGlobalRecurringDateConfigFields, (field) => {
        if (!_.includes(latestEditedRecurringDateConfigFields, field)) {
          return {
            value: field,
            label: field,
          };
        }
        return null;
      }),
    );
  }, [globalRecurringDateConfigs, latestEditedRecurringDateConfigFields]);

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[200]}
        borderRadius="8px"
        padding="32px"
      >
        {isEditModeOpen && !!wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <>
            <Text color={textColor} mb="24px">
              You can add multiple options, one at a time.
            </Text>
            <Flex alignItems="center" justifyContent="space-between">
              <Select
                value=""
                placeholderLabel={
                  selectOptions.length
                    ? 'Select'
                    : recurringDateConfigFieldPlaceholder
                }
                options={
                  selectOptions.length
                    ? selectOptions
                    : placeholderSelectOptions
                }
                isDisabled={!selectOptions.length}
                onChange={(value) => {
                  if (value) {
                    onAddRecurringDateConfigField({
                      newRecurringDateConfigField: value,
                    });
                  }
                }}
                width="100%"
              />
              <>
                <Button
                  color={colors.aurora[400]}
                  border="2px"
                  borderRadius="8px"
                  borderColor={colors.aurora[400]}
                  backgroundColor="white"
                  onClick={() => {
                    setLatestEditedRecurringDateConfigFields(
                      recurringQuery.globalRecurringDateConfigFields,
                    );
                    onEditModeClose();
                  }}
                  ml="32px"
                  mr="24px"
                >
                  Cancel
                </Button>
                <Tooltip
                  label={
                    latestEditedRecurringDateConfigFields.length
                      ? ''
                      : 'A Single Date Query should be configured instead of a Recurring Date Query'
                  }
                >
                  <span>
                    <Button
                      color="white"
                      backgroundColor={colors.aurora[400]}
                      isLoading={isSaveRecurringDateConfigsLoading}
                      isDisabled={!latestEditedRecurringDateConfigFields.length}
                      onClick={async () => {
                        await onSaveRecurringDateConfig();
                        onEditModeClose();
                      }}
                      p="8px 24px"
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              </>
            </Flex>
            <HStack alignItems="center" mt="24px">
              {_.map(
                latestEditedRecurringDateConfigFields,
                (latestStateField) => (
                  <Flex
                    key={`recurring-date-config-button-${latestStateField}`}
                    alignItems="center"
                    color={textColor}
                    cursor="default"
                    mr="24px"
                  >
                    <Text fontWeight={400} mr="10px">
                      {latestStateField}
                    </Text>
                    <Tooltip label={`Delete ${latestStateField}`}>
                      <Flex
                        cursor="pointer"
                        onClick={() =>
                          onDeleteRecurringDateConfigField({
                            recurringDateConfigFieldToDelete: latestStateField,
                          })
                        }
                      >
                        <Icon icon="x" size="12px" />
                      </Flex>
                    </Tooltip>
                  </Flex>
                ),
              )}
            </HStack>
            {!latestEditedRecurringDateConfigFields.length && (
              <Text color={textColor}>
                Must have at least one recurring date. If Recurring Dates are
                not required, a Single Date Query should be configured instead
                of a Recurring Date Query.
              </Text>
            )}
          </>
        ) : (
          <Flex justifyContent="space-between">
            <VStack spacing="16px" alignItems="flex-start" alignSelf="center">
              {_.map(
                recurringQuery.globalRecurringDateConfigFields,
                (existingRecurringDateConfigField) => (
                  <Text
                    key={existingRecurringDateConfigField}
                    color={textColor}
                    textAlign="left"
                  >
                    {existingRecurringDateConfigField}
                  </Text>
                ),
              )}
            </VStack>
            <EditIconButton
              label="Edit Recurring Dates"
              ariaLabel="edit recurring dates"
              onClick={onEditModeOpen}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
